import { useState, useMemo, useEffect } from "react";

import {
  Box,
  Text,
  Breadcrumb,
  BreadcrumbItem,
  Container,
  Heading,
  Stack,
  //   VStack,
  HStack,
  FormHelperText,
  FormControl,
  useToast,
  useDisclosure,
  CircularProgress,
} from "@chakra-ui/react";

import { useHistory } from "react-router-dom";

import ArrowForwardIosRoundedIcon from "@material-ui/icons/ArrowForwardIosRounded";
import Header from "components/dashHeader";
import Sidemenu from "components/sidemenu";
import Button from "components/Button/Button";
import Select from "components/Select/Select";
import Input from "components/Input/Input";
import SuccessModal from "components/Modals/Success";
import BreadcrumbLink from "components/BreadcrumbLink/BreadcrumbLink";

import {
  useGetCoinToUsdRateQuery,
  useCreatePriceAlertMutation,
} from "services/rateApi";

import constant from "config/constant";
import toPrecision from "util/toPrecision";
import toCoinLocale from "util/toCoinLocale";

export default function AddCryptoAlert(props) {
  //   const { userData } = props;

  const toast = useToast();
  const history = useHistory();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [state, _setState] = useState({ type: "price" });

  const setState = (newState) => _setState(() => ({ ...state, ...newState }));

  const [createAlert, { data, isLoading, isSuccess, isError, error }] =
    useCreatePriceAlertMutation();

  const { data: rate, isFetching: isFetchingRate } = useGetCoinToUsdRateQuery(
    state?.coin,
    { skip: !state?.coin }
  );

  const buyRate = useMemo(() => rate?.exchangeRate?.buy, [rate]);

  // console.log("RATE", buyRate);

  const alertTypeLabel = useMemo(() => {
    const map = {
      price: {
        label: "Price",
        placeholder: "Enter price",
      },
      percentage: {
        label: "Percentage (%)",
        placeholder: "Enter percentage",
      },
    };

    return map[state?.type ?? "price"];
  }, [state?.type]);

  const handleSubmit = (e) => {
    e.preventDefault();
    createAlert({ ...state, ticker: `${state?.coin}usdt` });
  };

  const handleClose = () => {
    onClose();
    setState({});
    history.replace("/dashboard/account/pricealert");
  };

  const isDisabled = useMemo(
    () =>
      !(state?.coin && state?.direction && state?.price && state?.type) ||
      isLoading,
    [state, isLoading]
  );

  const getError = (error) => {
    function isObj(obj) {
      var type = typeof obj;
      return type === "function" || (type === "object" && !!obj);
    }
    const value = isObj(error) ? Object.values(error)[0] : error;
    // console.log("ERROR VALUE", value);
    return value ?? "An unexpected error occurred";
  };

  useEffect(() => {
    if (isSuccess && data) {
      onOpen();
    }

    if (isError && error) {
      toast({
        position: "bottom-right",
        title: "Error",
        description: getError(error?.data?.error),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  }, [isSuccess, isError, data, error, onOpen, toast]);

  return (
    <Box>
      <Sidemenu currentPage="Account" logoutUser={props.logout} />
      <Header />
      <Box as="section">
        <Box
          className="account-container"
          p={["6em 0 !important", "6.5em 1.5em !important"]}
        >
          <Container
            maxW={["100%", "100%", "calc(100% - 15vw)", "calc(100% - 35vw)"]}
            m="0"
            p={[0, 0, "1rem"]}
          >
            <Stack mx={["24px", 0]} mb={["24px", "32px"]}>
              <Heading>Add Crypto Price Alert</Heading>
              <Breadcrumb
                className="animate__animated animate__fadeInDown"
                separator={
                  <ArrowForwardIosRoundedIcon
                    style={{ fontSize: ".8em", color: "#6E7191" }}
                  />
                }
              >
                <BreadcrumbItem>
                  <BreadcrumbLink to="/dashboard/account">
                    Account
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem>
                  <BreadcrumbLink to="/dashboard/account/pricealert">
                    Crypto Price Alert
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage>
                  <BreadcrumbLink isCurrentPage to="">
                    Add Crypto Price Alert
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </Breadcrumb>
            </Stack>

            <Box
              bg="white"
              p={["32px 24px", "32px 24px", "64px"]}
              //   mt={["24px", "10px"]}
              borderRadius={[0, "8px", "16px"]}
            >
              <Stack as="form" onSubmit={handleSubmit}>
                <FormControl>
                  <Select
                    label="Coin Type"
                    placeholder="Select"
                    value={state?.coin ?? ""}
                    onChange={(e) => setState({ coin: e.target.value })}
                  >
                    {/* <option value="btc">BTC</option>
                    <option value="eth">ETH</option>
                    <option value="celo">CELO</option> */}
                    {constant.priceAlertCoins.map((coin) => (
                      <option value={coin}>{toCoinLocale(coin)}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <Select
                    label="Alert Type"
                    placeholder="Select alert type"
                    value={state?.type ?? ""}
                    onChange={(e) => setState({ type: e.target.value })}
                  >
                    <option value="price">Price</option>
                    <option value="percentage">Percentage</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <Select
                    label="Movement"
                    placeholder="Select"
                    value={state?.direction ?? ""}
                    onChange={(e) => setState({ direction: e.target.value })}
                  >
                    <option value="up">Above</option>
                    <option value="down">Below</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <Input
                    label={alertTypeLabel?.label}
                    placeholder={alertTypeLabel?.placeholder}
                    value={state?.price ?? ""}
                    onChange={(e) => setState({ price: e.target.value })}
                  />

                  {state?.coin && (
                    <FormHelperText fontSize="sm">
                      <HStack>
                        <Text>Current price in USD:</Text>
                        {buyRate && !isFetchingRate && (
                          <Text>{toPrecision(buyRate, "usd")}</Text>
                        )}
                        {isFetchingRate && (
                          <CircularProgress
                            size="12px"
                            color="green.400"
                            isIndeterminate
                          />
                        )}
                      </HStack>
                    </FormHelperText>
                  )}
                </FormControl>

                <Button
                  mt="56px !important"
                  minW="320px"
                  minH="64px"
                  type="submit"
                  isLoading={isLoading}
                  disabled={isDisabled}
                >
                  Save alert
                </Button>
              </Stack>
            </Box>
          </Container>
        </Box>
      </Box>

      <SuccessModal
        isOpen={isOpen}
        onClose={handleClose}
        title="Success"
        message={`Price alert successfully created `}
      />
    </Box>
  );
}
